import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, Paper, TextField, Tooltip } from '@mui/material';
import { useDebounce } from '@uidotdev/usehooks';

import { callApi } from 'utils';

export const toInputEvent = (name, value) => ({
  target: {
    name,
    value,
  },
});

const sxArray = (sx) => {
  if (!sx) return [];
  return Array.isArray(sx) ? sx : [sx];
};

const autocompleteSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 32,
    py: 0,
  },
  '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
    px: '6px',
    py: '6px',
  },
  '& .MuiAutocomplete-endAdornment': {
    right: 6,
  },
};

const RemoteAutocomplete = ({
  autoHighlight = false,
  buildUrl,
  getOptionKey,
  getOptionLabel,
  getOptionValue,
  hydrateSelectedOption = false,
  lockSelectedOption = true,
  name,
  onChange,
  placeholder,
  renderOptionLabel,
  renderSelectedLabel,
  sx,
  value,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const debouncedSearch = useDebounce(search, 300);
  const committedSelectedOption = useMemo(
    () => (
      selectedOption && String(getOptionValue(selectedOption)) === String(value)
        ? selectedOption
        : null
    ),
    [getOptionValue, selectedOption, value]
  );
  const clearSelection = () => {
    setSelectedOption(null);
    setSearch('');
    setOptions([]);
    onChange(toInputEvent(name, ''));
  };

  const selectedValue = committedSelectedOption;
  const selectedInputValue = selectedValue
    ? String((renderSelectedLabel || renderOptionLabel || getOptionLabel)(selectedValue) || '')
    : value || '';
  const hasSelectedValue = lockSelectedOption && Boolean(selectedValue);

  useEffect(() => {
    if (!value) {
      setSearch('');
      setSelectedOption(null);
    } else if (selectedOption && String(getOptionValue(selectedOption)) !== String(value)) {
      setSelectedOption(null);
    }
  }, [getOptionValue, selectedOption, value]);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setOptions([]);

    callApi(buildUrl(debouncedSearch.trim()))
      .then((res) => {
        active && setOptions(res.rows || []);
      })
      .catch(console.error)
      .finally(() => {
        active && setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [buildUrl, debouncedSearch, open]);

  useEffect(() => {
    if (!hydrateSelectedOption || !value || selectedValue || open) {
      return undefined;
    }

    let active = true;
    callApi(buildUrl(String(value)))
      .then((res) => {
        if (!active) return;

        const matchedOption = (res.rows || []).find(
          (option) => String(getOptionValue(option)) === String(value)
        );
        if (matchedOption) {
          setSelectedOption(matchedOption);
        }
      })
      .catch(console.error);

    return () => {
      active = false;
    };
  }, [buildUrl, getOptionValue, hydrateSelectedOption, open, selectedValue, value]);

  return (
    <Autocomplete
      autoHighlight={autoHighlight}
      filterOptions={(opts) => opts}
      forcePopupIcon={false}
      freeSolo
      getOptionLabel={(option) => typeof option === 'string' ? option : String(getOptionLabel(option) || '')}
      inputValue={selectedInputValue}
      isOptionEqualToValue={(option, selected) => {
        const selectedOptionValue = typeof selected === 'string' ? selected : getOptionValue(selected);
        return String(getOptionValue(option)) === String(selectedOptionValue);
      }}
      loading={loading}
      loadingText="搜索中..."
      noOptionsText="无匹配结果"
      onChange={(_, option) => {
        const nextValue = typeof option === 'string' ? option : option ? getOptionValue(option) : '';
        setSelectedOption(typeof option === 'string' ? null : option);
        setSearch('');
        onChange(toInputEvent(name, String(nextValue)));
      }}
      onClose={() => setOpen(false)}
      onInputChange={(_, nextValue, reason) => {
        if (reason === 'input') {
          setSelectedOption(null);
          setSearch(nextValue);
          onChange(toInputEvent(name, nextValue));
        } else if (reason === 'clear') {
          setSelectedOption(null);
          setSearch('');
          setOptions([]);
          onChange(toInputEvent(name, ''));
        } else if (reason === 'reset') {
          setSearch('');
        }
      }}
      onOpen={() => setOpen(true)}
      openOnFocus
      options={options}
      PaperComponent={(props) => (
        <Paper {...props}>
          {props.children}
          {!loading && open && debouncedSearch.trim() && options.length === 0 && (
            <Box sx={{color: 'text.secondary', fontSize: 14, px: 2, py: 1}}>
              无匹配结果
            </Box>
          )}
        </Paper>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            name,
            readOnly: hasSelectedValue,
            onKeyDown: (ev) => {
              const hasHighlightedOption = Boolean(ev.currentTarget.getAttribute('aria-activedescendant'));
              if (hasSelectedValue && (ev.key === 'Backspace' || ev.key === 'Delete')) {
                ev.preventDefault();
                ev.stopPropagation();
                clearSelection();
                return;
              }
              params.inputProps.onKeyDown?.(ev);
              if (
                ev.key === 'Enter'
                && !ev.nativeEvent.isComposing
                && !hasHighlightedOption
                && !ev.defaultPrevented
                && ev.currentTarget.form
              ) {
                ev.preventDefault();
                ev.currentTarget.form.requestSubmit();
              }
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          placeholder={placeholder}
          size="small"
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const optionLabel = renderOptionLabel ? renderOptionLabel(option) : getOptionLabel(option);
        return (
          <li key={getOptionKey(option) || key} {...optionProps}>
            <Tooltip title={optionLabel || ''} placement="right" disableInteractive>
              <span style={{display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%'}}>
                {optionLabel}
              </span>
            </Tooltip>
          </li>
        );
      }}
      sx={[autocompleteSx, ...sxArray(sx)]}
      value={selectedValue}
    />
  );
};

export default RemoteAutocomplete;
